import { Timestamp } from 'firebase/firestore';
import { Mention } from './mention';

// ==================== Base Types ====================

export type ConversationType = 'direct' | 'group';
export type MessageType = 'text' | 'image' | 'file' | 'genealogy_link';
export type NotificationLevel = 'all' | 'mentions' | 'none';

// ==================== Conversation Types ====================

export interface Conversation {
  id: string;
  type: ConversationType;
  participants: string[];

  // Group-specific (null for direct)
  name?: string;
  description?: string;
  avatar?: string;
  creator?: string;
  admins?: string[];
  settings?: GroupSettings;

  // Member info map
  memberInfo: Record<string, MemberInfo>;

  // Last message preview
  lastMessage?: LastMessagePreview;
  pinnedMessages?: string[];

  // Timestamps
  lastActivity: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface GroupSettings {
  anyoneCanAdd: boolean;
  anyoneCanChangeName: boolean;
  anyoneCanChangeAvatar: boolean;
}

export interface MemberInfo {
  nickname?: string;
  joined: Date;
  addedBy: string;
  lastRead?: Date;
  unreadCount: number;
  muted: boolean;
  notifications: NotificationLevel;
  archived: boolean;
}

export interface LastMessagePreview {
  text: string;
  senderId: string;
  timestamp: Date;
  type: MessageType;
}

// ==================== Message Types ====================

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  type: MessageType;
  content: string;
  attachments?: Attachment[];
  replyTo?: string;
  reactions?: Record<string, string[]>;
  readBy: string[];
  mentions?: Mention[];
  edited: boolean;
  editedAt?: Date;
  deleted: boolean;
  deletedAt?: Date;
  deletedBy?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface Attachment {
  id: string;
  type: 'image' | 'file';
  url: string;
  thumbnailUrl?: string;
  fileName: string;
  fileType: string;
  size: number;
  width?: number;
  height?: number;
}

export interface GenealogyLinkContent {
  linkType: 'person' | 'photo' | 'event' | 'document';
  targetId: string;
  preview: {
    title: string;
    description?: string;
    thumbnailUrl?: string;
  };
}

// ==================== Message Preferences ====================

export interface MessagePreferences {
  userId: string;
  enabled: boolean;
  pausedUntil?: Date;
  channels: {
    inApp: { enabled: boolean; sound: boolean; preview: boolean };
    email: {
      enabled: boolean;
      frequency: 'instant' | 'hourly' | 'daily' | 'never';
      quietHours: { enabled: boolean; start: string; end: string };
    };
    push?: { enabled: boolean; sound: boolean };
  };
  directMessages: {
    notify: 'all' | 'none';
    sound: boolean;
    readReceipts: boolean;
    typingIndicators: boolean;
  };
  groupChats: {
    notify: NotificationLevel;
    sound: boolean;
    readReceipts: boolean;
    typingIndicators: boolean;
  };
  blockedUsers: string[];
  whoCanMessageMe: 'everyone' | 'family_only';
  autoArchive: { enabled: boolean; afterDays: number };
  updatedAt: Date;
  version: number;
}

// ==================== DTOs for Service Methods ====================

export interface CreateConversationParams {
  type: ConversationType;
  participants: string[];
  name?: string;
  description?: string;
  settings?: Partial<GroupSettings>;
}

export interface CreateMessageParams {
  conversationId: string;
  type: MessageType;
  content: string;
  attachments?: Omit<Attachment, 'id'>[];
  replyTo?: string;
  mentions?: Mention[];
}

export interface UpdateMessageParams {
  content?: string;
  edited?: boolean;
  editedAt?: Date;
}

export interface AddReactionParams {
  messageId: string;
  emoji: string;
  userId: string;
}

export interface UpdateMemberInfoParams {
  nickname?: string;
  muted?: boolean;
  notifications?: NotificationLevel;
  archived?: boolean;
}

// ==================== Search & Filter Types ====================

export interface MessageSearchParams {
  conversationId?: string;
  senderId?: string;
  startDate?: Date;
  endDate?: Date;
  types?: MessageType[];
  searchTerm?: string;
  limit?: number;
}

export interface ConversationFilters {
  type?: ConversationType;
  includeArchived?: boolean;
  onlyUnread?: boolean;
  limit?: number;
}

// ==================== Typing Indicator Types ====================

export interface TypingIndicator {
  conversationId: string;
  typing: Record<string, {
    startedAt: Date;
    expiresAt: Date;
  }>;
}

// ==================== Validation Errors ====================

export class MessageValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MessageValidationError';
  }
}

export class ConversationValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversationValidationError';
  }
}

// ==================== Default Preferences ====================

export const DEFAULT_MESSAGE_PREFERENCES: Omit<MessagePreferences, 'userId'> = {
  enabled: true,
  channels: {
    inApp: { enabled: true, sound: true, preview: true },
    email: {
      enabled: true,
      frequency: 'daily',
      quietHours: { enabled: true, start: '22:00', end: '08:00' }
    }
  },
  directMessages: {
    notify: 'all',
    sound: true,
    readReceipts: true,
    typingIndicators: true
  },
  groupChats: {
    notify: 'mentions',
    sound: true,
    readReceipts: true,
    typingIndicators: true
  },
  blockedUsers: [],
  whoCanMessageMe: 'everyone',
  autoArchive: { enabled: false, afterDays: 90 },
  updatedAt: new Date(),
  version: 1
};
